import {
    useState,
    useEffect,
    useRef,
    useLayoutEffect,
    type ChangeEvent,
} from "react";
import {ActionIcon, Tooltip} from "@mantine/core";
import {useTranslation} from "react-i18next";
import DeleteSvg from "../../../../assets/v2-bin.svg?react";
import {useChecklistData} from "../ChecklistDataContext";
import {useComment} from "./CommentContext";
import {dibApi} from "../../../chat/dibApi";
import styles from "./comment-panel.module.css";

const dateFormatter = new Intl.DateTimeFormat("nb", {dateStyle: "medium"});
const formatDate = (d: string) => {
    try {
        return dateFormatter.format(new Date(d));
    } catch {
        return d;
    }
};

const getInitials = (name: string) => {
    const parts = name.split(" ").filter(Boolean);
    if (parts.length === 0) return "";
    if (parts.length === 1) return parts[0][0];
    return parts[0][0] + parts[parts.length - 1][0];
};

export const CommentGroup = ({id, title}: {id: string; title: string}) => {
    const {t} = useTranslation();
    const {checklistConfig, collectionData} = useChecklistData();
    const topicId = checklistConfig.metadata.topicId;
    const collectionId = collectionData?.metadata?.collectionId ?? "";

    const {focusedNode, setFocusedNode} = useComment();

    const {data: commentsData} = dibApi.endpoints.getComments.useQuery(
        {topicId, collectionId},
        {skip: !collectionId},
    );

    const [deleteComment, deleteResult] =
        dibApi.endpoints.deleteComment.useMutation();
    const [addComment, addResult] =
        dibApi.endpoints.addComment.useMutation();
    const [updateComment, updateResult] =
        dibApi.endpoints.updateComment.useMutation();

    const commentObjId = commentsData?.elementdata?.[id]?.ci_comments?.[0];
    const comment = commentObjId
        ? commentsData?.objects?.[commentObjId]
        : undefined;

    const isFocused = focusedNode?.id === id;
    const isNew = isFocused && focusedNode?.isNew === true;

    const [value, setValue] = useState(comment?.data?.comment ?? "");
    const hasChanged = useRef(false);
    const initialValue = useRef(comment?.data?.comment ?? "");
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Focus textarea when this item is focused
    useEffect(() => {
        if (isFocused && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [isFocused]);

    // Auto-resize textarea
    useLayoutEffect(() => {
        setTimeout(() => {
            const el = textareaRef.current;
            if (el && el.scrollHeight > el.getBoundingClientRect().height) {
                el.style.height = `${el.scrollHeight}px`;
            }
        }, 0);
    }, [value]);

    const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        hasChanged.current = true;
        setValue(e.target.value);
    };

    const handleBlur = () => {
        if (isNew && value) {
            addComment({topicId, collectionId, id, comment: value});
        } else if (
            hasChanged.current &&
            value !== initialValue.current &&
            commentObjId
        ) {
            updateComment({
                topicId,
                collectionId,
                id,
                comment: value,
                commentId: commentObjId,
            });
        }
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (commentObjId) {
            deleteComment({topicId, collectionId, commentId: commentObjId});
            setFocusedNode(undefined, false);
        }
    };

    const scrollToItem = () => {
        document
            .querySelector(`[data-id="${id}"]`)
            ?.scrollIntoView({behavior: "auto", block: "start"});
    };

    // Error handling — rollback local state on failure
    useEffect(() => {
        if (addResult.isError) {
            setValue("");
        }
    }, [addResult.isError]);

    useEffect(() => {
        if (updateResult.isError) {
            setValue(initialValue.current);
        }
    }, [updateResult.isError]);

    // Guard: skip if no comment data and not focused
    if (!commentsData?.elementdata?.[id] && !isFocused) return null;

    const name = comment?.data?.editedBy ?? "";

    return (
        <li>
            <section
                className={`${styles.commentGroup}${isFocused ? ` ${styles.focused}` : ""}`}
            >
                <a
                    href={`#${id}`}
                    className={styles.commentTitle}
                    onClick={(e) => {
                        e.preventDefault();
                        scrollToItem();
                    }}
                    title={title}
                >
                    {title || t("Word.Untitled")}
                </a>

                <div className={styles.commentItem}>
                    {name && (
                        <div className={styles.initials}>
                            {getInitials(name)}
                        </div>
                    )}
                    <div className={styles.commentWrapper}>
                        {comment && (
                            <div className={styles.commentMeta}>
                                <span>
                                    {comment.data?.editedBy && (
                                        <span className={styles.username}>
                                            {comment.data.editedBy}
                                        </span>
                                    )}
                                    {comment.data?.editDate && (
                                        <span className={styles.timestamp}>
                                            {formatDate(
                                                comment.data.editDate,
                                            )}
                                        </span>
                                    )}
                                </span>
                                {comment.data?.editedBy && (
                                    <Tooltip label={t("Action.DeleteComment")}>
                                        <ActionIcon
                                            color="red"
                                            variant="subtle"
                                            size="sm"
                                            onClick={handleDelete}
                                            disabled={
                                                deleteResult.isLoading ||
                                                deleteResult.isSuccess
                                            }
                                        >
                                            <DeleteSvg
                                                width={16}
                                                height={16}
                                            />
                                        </ActionIcon>
                                    </Tooltip>
                                )}
                            </div>
                        )}
                        <textarea
                            ref={textareaRef}
                            className={styles.commentTextarea}
                            value={value}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder={t("Action.WriteAComment")}
                        />
                    </div>
                </div>
            </section>
        </li>
    );
};
